import type {
  ApiResponse,
  DataFileListItem,
  DataFileSummaryStats,
  ListDataFilesQuery,
  PaginatedResponse,
  UploadDataFileResponse,
} from '@kangaroo/shared';

import { api } from '../lib/api';
import { buildMyChronSessionFromGpxFile } from '../lib/xrzToGpx';
import { parseSessionExportJson } from '../lib/parseSessionExportJson';
import {
  clearSessionContentCache,
  readCachedSessionContent,
  writeCachedSessionContent,
} from '../lib/sessionContentCache';
import { clearSessionGeometryCache } from '../lib/sessionGeometryCache';
import { invalidateSessionListCache } from '../lib/sessionListCache';
import type { DataFileContentProfile } from '../lib/telemetryCachePolicy';
import { OVERVIEW_CONTENT_VERSION } from '../lib/telemetryCachePolicy';
import { sessionMapDebug, sessionMapDebugDuration } from '../lib/sessionMapDebug';
import { clearSessionArtifactCache } from './sessions/sessionArtifacts';

export type { DataFileContentProfile } from '../lib/telemetryCachePolicy';

const dataFileContentCache = new Map<string, unknown>();
const dataFileContentRequests = new Map<string, Promise<unknown>>();

function contentCacheKey(fileId: string, profile: DataFileContentProfile) {
  return `${fileId}:${profile}`;
}

export function invalidateDataFileContentCache(fileId?: string) {
  if (!fileId) {
    dataFileContentCache.clear();
    return;
  }

  dataFileContentCache.delete(contentCacheKey(fileId, 'overview'));
  dataFileContentCache.delete(contentCacheKey(fileId, 'full'));
}

export function clearDataFileContentCache() {
  dataFileContentCache.clear();
  dataFileContentRequests.clear();
  clearSessionArtifactCache();
  clearSessionGeometryCache();
  return clearSessionContentCache();
}

export async function fetchDataFileContent(
  fileId: string,
  profile: DataFileContentProfile = 'full',
  options?: { force?: boolean },
) {
  const cacheKey = contentCacheKey(fileId, profile);

  if (!options?.force) {
    if (dataFileContentCache.has(cacheKey)) {
      sessionMapDebug('fetchDataFileContent memory hit', { fileId, profile });
      return dataFileContentCache.get(cacheKey);
    }

    const persisted = await readCachedSessionContent(fileId, profile);
    if (persisted !== null && persisted !== undefined) {
      sessionMapDebug('fetchDataFileContent IDB hit', { fileId, profile });
      dataFileContentCache.set(cacheKey, persisted);
      return persisted;
    }
  }

  const inFlight = dataFileContentRequests.get(cacheKey);
  if (inFlight && !options?.force) {
    sessionMapDebug('fetchDataFileContent awaiting in-flight', { fileId, profile });
    return inFlight;
  }

  const startedAt = performance.now();
  sessionMapDebug('fetchDataFileContent network start', {
    fileId,
    profile,
    version: OVERVIEW_CONTENT_VERSION,
  });

  const request = api
    .get<string>(`/data-files/${fileId}/content`, {
      params: profile === 'overview' ? { profile, v: OVERVIEW_CONTENT_VERSION } : { profile },
      responseType: 'text',
      transformResponse: [(data) => data],
    })
    .then(async (response) => {
      const parseStartedAt = performance.now();
      const payload = await parseSessionExportJson(response.data);
      sessionMapDebugDuration('fetchDataFileContent parse done', parseStartedAt, {
        fileId,
        profile,
        bytes: response.data.length,
      });
      dataFileContentCache.set(cacheKey, payload);
      if (profile === 'full') {
        dataFileContentCache.set(contentCacheKey(fileId, 'overview'), payload);
      }
      void writeCachedSessionContent(fileId, profile, payload);
      sessionMapDebugDuration('fetchDataFileContent network done', startedAt, { fileId, profile });
      return payload;
    })
    .catch((error) => {
      sessionMapDebugDuration('fetchDataFileContent network failed', startedAt, {
        fileId,
        profile,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    })
    .finally(() => {
      dataFileContentRequests.delete(cacheKey);
    });

  dataFileContentRequests.set(cacheKey, request);
  return request;
}

export async function fetchDataFileRawContent(fileId: string) {
  const response = await api.get(`/data-files/${fileId}/raw-content`);
  return response.data;
}

export async function downloadDataFile(file: DataFileListItem) {
  const response = await api.get(`/data-files/${file.id}/content`, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = file.originalName;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

export async function fetchDataFiles(
  query: ListDataFilesQuery & { snapshot?: 'new' | string } = {},
) {
  const response = await api.get<PaginatedResponse<DataFileListItem> & { snapshot?: string }>(
    '/data-files',
    { params: query },
  );
  return response.data;
}

export async function fetchDataFile(fileId: string) {
  const response = await api.get<ApiResponse<DataFileListItem>>(
    `/data-files/${encodeURIComponent(fileId)}`,
  );
  return response.data.data;
}

export async function fetchDataFileStats() {
  const response = await api.get<ApiResponse<DataFileSummaryStats>>('/data-files/stats/summary');
  return response.data.data;
}

export type MyChronUploadContext = {
  driverId?: string | null;
  kartId?: string | null;
};

export async function uploadMyChronDataFile(
  file: File,
  context: MyChronUploadContext = {},
  options: { onProgress?: (progress: number) => void } = {},
) {
  const formData = new FormData();
  formData.append('file', file);
  if (context.driverId) {
    formData.append('driverId', context.driverId);
  }
  if (context.kartId) {
    formData.append('kartId', context.kartId);
  }

  const response = await api.post<ApiResponse<UploadDataFileResponse>>(
    '/data-files/mychron',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        const progress = event.progress
          ?? (event.total && event.total > 0 ? event.loaded / event.total : null);
        if (progress !== null) options.onProgress?.(Math.max(0, Math.min(1, progress)));
      },
    },
  );
  invalidateSessionListCache();
  return response.data.data.file;
}

export async function uploadGpxAsMyChronSession(file: File, context: MyChronUploadContext = {}) {
  const { session, sessionName } = await buildMyChronSessionFromGpxFile(file);
  const jsonFile = new File([JSON.stringify(session)], `${sessionName}.json`, {
    type: 'application/json',
  });

  return uploadMyChronDataFile(jsonFile, context);
}

export async function uploadOtherDataFile(file: File, context: MyChronUploadContext = {}) {
  const formData = new FormData();
  formData.append('file', file);
  if (context.driverId) {
    formData.append('driverId', context.driverId);
  }
  if (context.kartId) {
    formData.append('kartId', context.kartId);
  }

  const response = await api.post<ApiResponse<UploadDataFileResponse>>(
    '/data-files/other',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  invalidateSessionListCache();
  return response.data.data.file;
}

export async function deleteDataFile(fileId: string) {
  await api.delete(`/data-files/${encodeURIComponent(fileId)}`);
  invalidateDataFileContentCache(fileId);
  invalidateSessionListCache();
}

export function dataFilesListKey(query: ListDataFilesQuery) {
  return ['data-files', query] as const;
}

export function dataFileDetailKey(fileId: string) {
  return ['data-files', fileId] as const;
}

export const dataFileStatsKey = ['data-files', 'stats'] as const;
